import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { 
  format, 
  startOfMonth, 
  endOfMonth, 
  eachDayOfInterval, 
  getDay, 
  isSameDay, 
  addMonths, 
  subMonths 
} from 'date-fns';
import {
  useTheme,
  useMediaQuery,
  Box,
  Typography,
  Tooltip,
  IconButton,
  Chip,
  CircularProgress,
  Paper,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import { 
  AddCircleOutline, 
  Edit,
  Delete,
  DeleteForever,
  CalendarMonth,
  List,
  ChevronLeft,
  ChevronRight,
} from '@mui/icons-material';
import useAuthCheck from 'hooks/useAuthCheck';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import { LAYOUT_HEIGHT } from 'state/constants';
import api from 'state/axios';
import { 
  buildPermissionMap, 
  hasRight,
} from 'state/checkPermission';
import { getSelectedIds, isAnyRowSelected } from 'state/helpers';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import Header from 'components/Header';
import ScrollToTop from 'components/ScrollToTop';
import StyledButton from 'components/StyledButton';
import DeleteConfirm from 'components/DeleteConfirm';
import CardList from 'components/CardList';
import DataGridComponent from 'components/DataGridComponent';


const STATUS_COLOR = {
  planned: 'default',
  active: 'primary',
  completed: 'success',
  cancelled: 'error',
};

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const CalendarView = ({ sessions, onEdit }) => {
  const [month, setMonth] = useState(new Date());
  const theme = useTheme();

  const days = useMemo(() => {
    const start = startOfMonth(month);
    const end = endOfMonth(month);
    return eachDayOfInterval({ start, end });
  }, [month]);

  const startPadding = getDay(startOfMonth(month));

  const sessionsByDay = useMemo(() => {
    const map = {};
    for (const s of sessions) {
      const key = format(new Date(s.startDate), 'yyyy-MM-dd');
      if (!map[key]) map[key] = [];
      map[key].push(s);
    }
    return map;
  }, [sessions]);

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <IconButton size="small" onClick={() => setMonth((m) => subMonths(m, 1))}>
          <ChevronLeft />
        </IconButton>
        <Typography variant="subtitle1" fontWeight="bold" sx={{ minWidth: 160, textAlign: 'center' }}>
          {format(month, 'MMMM yyyy')}
        </Typography>
        <IconButton size="small" onClick={() => setMonth((m) => addMonths(m, 1))}>
          <ChevronRight />
        </IconButton>
      </Box>

      <Box
        display="grid"
        gridTemplateColumns="repeat(7, 1fr)"
        gap={0.5}
        mb={0.5}
      >
        {DAYS.map((d, i) => (
          <Typography
            key={d}
            variant="body2"
            textAlign="center"
            color={i === 0 ? '#f48fb1' : i === 6 ? '#90caf9' : 'text.primary'}
            fontWeight="bold"
          >
            {d}
          </Typography>
        ))}
      </Box>

      <Box display="grid" gridTemplateColumns="repeat(7, 1fr)" gap={0.5}>
        {Array.from({ length: startPadding }).map((_, i) => (
          <Box key={`pad-${i}`} />
        ))}
        {days.map((day) => {
          const key = format(day, 'yyyy-MM-dd');
          const daySessions = sessionsByDay[key] || [];
          const isToday = isSameDay(day, new Date());
          const dow = getDay(day);
          const isSunday = dow === 0;
          const isSaturday = dow === 6;
          return (
            <Paper
              key={key}
              elevation={0}
              sx={{
                minHeight: 60,
                p: 0.5,
                border: isSunday
                  ? `1px solid #f48fb1`
                  : isSaturday
                  ? `1px solid #90caf9`
                  : `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                bgcolor: isToday ? 'action.hover' : 'background.paper',
              }}
            >
              <Typography
                variant="body2"
                fontWeight={isToday ? 'bold' : 'normal'}
                color={isToday ? 'primary.main' : isSunday ? '#f48fb1' : isSaturday ? '#90caf9' : 'text.primary'}
              >
                {format(day, 'd')}
              </Typography>
              <Box display="flex" flexDirection="column" gap={0.25} mt={0.25}>
                {daySessions.slice(0, 3).map((s) => (
                  <Chip
                    key={s._id}
                    label={s.subjects?.[0]?.subjectCode || '-'}
                    size="small"
                    color={STATUS_COLOR[s.status] || 'default'}
                    onClick={() => onEdit(s._id)}
                    sx={{ fontSize: '0.6rem', height: 16, cursor: 'pointer' }}
                  />
                ))}
                {daySessions.length > 3 && (
                  <Typography variant="caption" color="text.secondary">
                    +{daySessions.length - 3} more
                  </Typography>
                )}
              </Box>
            </Paper>
          );
        })}
      </Box>
    </Box>
  );
};

const TrainingSessions = () => {
  useAuthCheck();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user);
  const theme = useTheme();
  const primaryDark = theme.palette.primary.dark;
  const greenDeep = theme.palette.green.deep;
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const permissionMap = useMemo(
    () => buildPermissionMap(user?.permissions),
    [user?.permissions]
  );
  const canCreate = hasRight(permissionMap, "TrainingSessions", "create", false);
  // const canEdit = hasRight(permissionMap, "TrainingSessions", "update", false);
  const canDelete = hasRight(permissionMap, "TrainingSessions", "delete", false);

  const { startDate, endDate } = useDateRangeContext();

  const [viewMode, setViewMode] = useState(0); // 0=list, 1=calendar
  const scrollRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [sessions, setSessions] = useState([]);
  const [total, setTotal] = useState(0);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 50 });
  const [rowSelectionModel, setRowSelectionModel] = useState({ type: 'include', ids: new Set() });
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [deleteType, setDeleteType] = useState(null);
  const [idToDelete, setIdToDelete] = useState(null);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({ _id: false });


  const fetchSessions = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: paginationModel.page + 1,
        limit: paginationModel.pageSize,
        from: startDate,
        to: endDate,
      };
      const res = await api.get('/trainingsessions', { params });
      const d = res.data;
      setSessions(d.data || d);
      setTotal(d.total || (d.data || d).length);
    } catch (err) {
      setError(err);
      toast.error('Failed to load sessions!');
    } finally {
      setLoading(false);
    }
  }, [paginationModel, startDate, endDate]);

  const fetchCalendar = useCallback(async () => {
    try {
      setLoading(true);
      const now = new Date();
      const from = format(subMonths(now, 1), 'yyyy-MM-dd');
      const to = format(addMonths(now, 3), 'yyyy-MM-dd');
      const res = await api.get('/trainingsessions/calendar', {
        params: { from, to },
      });
      setSessions(res.data || []);
    } catch (err) {
      toast.error('Failed to load calendar!');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (viewMode === 0) fetchSessions();
    else fetchCalendar();
  }, [viewMode, fetchSessions, fetchCalendar]);

  const executeDelete = useCallback(async () => {
    try {
      if (deleteType === 'single') {
        if (!idToDelete) return;
        await api.patch('/trainingsessions/soft-delete', { ids: [idToDelete] });
        setSessions((prev) => prev.filter((s) => s._id !== idToDelete));
        toast.success('Session deleted.');
      } else {
        const ids = getSelectedIds(rowSelectionModel, sessions);
        if (!ids.length) { toast.error('No rows selected!'); return; }
        await api.patch('/trainingsessions/soft-delete', { ids });
        setSessions((prev) => prev.filter((s) => !ids.includes(s._id)));
        toast.success(`${ids.length} session(s) deleted.`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Delete failed!');
    }
  }, [
    deleteType, 
    idToDelete, 
    rowSelectionModel, 
    sessions,
  ]);

  const columns = useMemo(() => [
    {
      field: 'no', 
      headerName: '#', 
      width: 60, 
      sortable: false, 
      filterable: false,
      renderCell: (p) => p.api.getAllRowIds().indexOf(p.id) + 1,
    },
    {
      field: 'trainingCourseId',
      headerName: 'Course',
      flex: 2,
      valueGetter: (val) => val?.courseName || '-',
    },
    {
      field: 'subjects', 
      headerName: 'Subject',
      ...(() => isMobile ? { width: 200 } : { flex: 2.5 })(),
      valueGetter: (val) => Array.isArray(val) && val.length
        ? val.map((s) => s.subjectCode).join(', ')
        : '-',
    },
    {
      field: 'startDate',
      headerName: 'StartDate',
      ...(() => isMobile ? { width: 110 } : { flex: 1.5 })(),
      valueFormatter: (value) => value ? format(new Date(value), 'dd/MM/yy') : '-',
    },
    {
      field: 'endDate', 
      headerName: 'EndDate',
      ...(() => isMobile ? { width: 110 } : { flex: 1.5 })(),
      valueFormatter: (value) => value ? format(new Date(value), 'dd/MM/yy') : '-',
    },
    {
      field: 'startTime', 
      headerName: 'StartTime',
      ...(() => isMobile ? { width: 80 } : { flex: 1 })(),
      valueFormatter: (value) => value || '-',
    },
    {
      field: 'endTime', 
      headerName: 'EndTime',
      ...(() => isMobile ? { width: 80 } : { flex: 1 })(),
      valueFormatter: (value) => value || '-',
    },
    {
      field: 'location', 
      headerName: 'Location',
      ...(() => isMobile ? { width: 120 } : { flex: 1.5 })(),
    },    
    {
      field: 'enrollments', 
      headerName: 'Enrolled',
      ...(() => isMobile ? { width: 80 } : { flex: 1 })(),
      valueGetter: (val) => `${(val || []).filter((e) => e.status !== 'withdrawn').length} / ${(val || []).length}`,
    },
    {
      field: 'status', 
      headerName: 'Status',
      ...(() => isMobile ? { width: 110 } : { flex: 1.2 })(),
      renderCell: ({ value }) => value ? (
        <Chip 
          label={value.charAt(0).toUpperCase() + value.slice(1)} 
          size="small" 
          color={STATUS_COLOR[value] || 'default'} 
          sx={{ flexShrink: 0, minWidth: 76, justifyContent: 'center' }}
        />
      ) : null,
    },
  ], [isMobile]);

  const actionColumn = [{
    field: 'action', 
    headerName: 'Action', 
    sortable: false, 
    filterable: false,
    width: canDelete ? 125 : 80,
    renderCell: (params) => (
      <FlexCenter gap={1} sx={{ width: '100%', height: '100%' }}>
        <Tooltip title="Edit">
          <IconButton
            onClick={() => navigate(`/trainingsessions/${params.row._id}`)}
            sx={{ 
              color: primaryDark, 
              transition: 'color 0.3s', 
              '&:hover': { color: greenDeep } 
            }}
          >
            <Edit />
          </IconButton>
        </Tooltip>
        {canDelete && (
          <Tooltip title="Delete">
            <IconButton
              onClick={() => { 
                setIdToDelete(params.row._id); 
                setDeleteType('single'); 
                setConfirmDialogOpen(true); 
              }}
              sx={{ 
                color: primaryDark, 
                transition: 'color 0.3s', 
                '&:hover': { color: greenDeep } 
              }}
            >
              <DeleteForever />
            </IconButton>
          </Tooltip>
        )}
      </FlexCenter>
    ),
  }];

  return (
    <Box className="main-container-box">
      <FlexBetween>
        <Header
          title="Training Sessions"
          subtitle="Schedule & Calendar"
        />
        <Box display="flex" alignItems="center" gap={1}>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, v) => v !== null && setViewMode(v)}
            size="small"
          >
            <Tooltip title="List view">
              <ToggleButton value={0}><List /></ToggleButton>
            </Tooltip>
            <Tooltip title="Calendar view">
              <ToggleButton value={1}><CalendarMonth /></ToggleButton>
            </Tooltip>
          </ToggleButtonGroup>
          {viewMode === 0 && (
            <Tooltip title="Bulk delete" arrow>
              <span>
                <StyledButton
                  disabled={!canDelete || !isAnyRowSelected(rowSelectionModel, sessions)}
                  onClick={() => { setDeleteType('bulk'); setConfirmDialogOpen(true); }}
                >
                  <Delete />
                </StyledButton>
              </span>
            </Tooltip>
          )}
          <Tooltip title="Create" arrow>
            <span>
              <StyledButton disabled={!canCreate} onClick={() => navigate('/trainingsessions/new')}>
                <AddCircleOutline />
              </StyledButton>
            </span>
          </Tooltip>
        </Box>
      </FlexBetween>
      
      <Box
        display="grid"
        gridTemplateColumns="repeat(12, minmax(0, 1fr))"
        gap={2}
      >
        <Box
          sx={{
            gridColumn: "span 12",
            height: LAYOUT_HEIGHT,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Box sx={{ flex: 1 }}>
            {loading && <FlexCenter height="100%"><CircularProgress size={28} /></FlexCenter>}
            {!loading && error && <FlexCenter height="100%"><Typography color="error">{error?.message}</Typography></FlexCenter>}
            {!loading && !error && viewMode === 0 && (
              isMobile ? (
                <CardList
                  rows={sessions.map((s) => ({
                    ...s,
                    _courseName: s.trainingCourseId?.courseName || '-',
                    _subjects: s.subjects?.map((sub) => sub.subjectCode).join(', ') || '',
                  }))}
                  primaryField="_courseName"
                  secondaryField="_subjects"
                  isActiveField={null}
                  statusChip={{
                    field: 'status',
                    colorMap: STATUS_COLOR,
                  }}
                  fields={[
                    { label: "Start Date", key: "startDate" },
                    { label: "End Date", key: "endDate" },
                    { label: "Start Time", key: "startTime" },
                    { label: "End Time", key: "endTime" },
                    { label: "Location", key: "location" },
                  ]}
                  canEdit
                  canDelete={canDelete}
                  onEdit={(id) => navigate(`/trainingsessions/${id}`)}
                  onDelete={(id) => { setIdToDelete(id); setDeleteType('single'); setConfirmDialogOpen(true); }}
                />
              ) : (
                <DataGridComponent
                  rows={sessions}
                  columns={columns.concat(actionColumn)}
                  columnVisibilityModel={columnVisibilityModel}
                  setColumnVisibilityModel={setColumnVisibilityModel}
                  loading={loading}
                  props={{
                    rowCount: total,
                    paginationMode: "server",
                    paginationModel,
                    onPaginationModelChange: setPaginationModel,
                    checkboxSelection: true,
                    disableRowSelectionOnClick: true,
                    rowSelectionModel,
                    onRowSelectionModelChange: setRowSelectionModel,
                  }}
                />
              )
            )}

            {!loading && !error && viewMode === 1 && (
              <Box ref={scrollRef} sx={{ height: '100%', overflowY: 'auto' }}>
                <CalendarView
                  sessions={sessions}
                  onEdit={(id) => navigate(`/trainingsessions/${id}`)}
                />
                <ScrollToTop scrollRef={scrollRef} />
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      <DeleteConfirm
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={executeDelete}
        deleteType={deleteType}
      />
    </Box>
  );
};

export default TrainingSessions;
